import { useEffect, useState } from "react";
import type { DealDetail, DealFieldDef, DealFieldsResponse } from "@edge/shared";
import { DEAL_FIELDS, isClosedLoanStage } from "@edge/shared";
import { fetchDealFields, ApiError } from "../../api";
import { ClosedLoanDetailsSection } from "./ClosedLoanDetailsSection";

interface DealOverviewProps {
  dealId: string;
  detail: DealDetail | null;
  loading: boolean;
}

function fmtMoney(n: number | null): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function fmtDate(iso: string | null): string {
  if (!iso) return "TBD";
  const d = new Date(`${iso}T12:00:00Z`);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

// Key fields highlighted per group (the at-a-glance anchors).
const HIGHLIGHT = new Set(["Contact_Name", "Loan_Amount_Requested", "Property_Location"]);

/** Format a raw Zoho value for read-only display, by curated widget. Empty → dash. */
function fmtByWidget(widget: DealFieldDef["widget"], raw: unknown): string {
  if (raw == null || raw === "") return "—";
  switch (widget) {
    case "currency":
      return fmtMoney(Number(raw));
    case "percent":
      return `${raw}%`;
    case "boolean":
      return raw ? "Yes" : "No";
    default:
      return String(raw);
  }
}

/** Resolve a curated field's display value from the /fields response. */
function fieldValue(f: DealFieldDef, data: DealFieldsResponse): string {
  if (f.apiName === "Contact_Name") return data.borrower?.name ?? "—";
  if (f.apiName === "Co_Borrower") return data.coBorrower?.name ?? "—";
  if (f.apiName === "Lender") return data.lender?.name ?? "—";
  if (f.computed) {
    const c = data.computed[f.apiName as keyof DealFieldsResponse["computed"]];
    if (c == null) return "—"; // LTV/LTARV empty until finalize → graceful dash
    return f.widget === "percent" ? `${c}%` : f.widget === "currency" ? fmtMoney(c) : String(c);
  }
  return fmtByWidget(f.widget, data.values[f.apiName]);
}

export function DealOverview({ dealId, detail, loading }: DealOverviewProps) {
  const [fields, setFields] = useState<DealFieldsResponse | null>(null);
  const [fieldsErr, setFieldsErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setFields(null);
    setFieldsErr(null);
    fetchDealFields(dealId)
      .then((d) => { if (live) setFields(d); })
      .catch((e) => {
        if (live) setFieldsErr(e instanceof ApiError ? `${e.code} (HTTP ${e.status})` : "load_failed");
      });
    return () => { live = false; };
  }, [dealId]);

  if (loading && !detail) {
    return (
      <div className="tab-content tab-content-loading">
        <div className="chat-thinking-label">LOADING DEAL…</div>
      </div>
    );
  }
  if (!detail) {
    return <div className="tab-content">Deal not found.</div>;
  }

  return (
    <div className="tab-content">
      <div className="fo-cols">
        {/* Left: Borrower + Property (the two short, stacked) + Existing Debt */}
        <div className="fo-col">
          {fields ? (
            <>
              <GroupBlock group="borrower" title="BORROWER" icon="◆" data={fields} />
              <GroupBlock group="property" title="PROPERTY" icon="▣" data={fields} />
              <GroupBlock group="existing_debt" title="EXISTING DEBT" icon="▤" data={fields} />
            </>
          ) : fieldsErr ? (
            <div className="section-block">
              <div className="section-title">DEAL FIELDS</div>
              <div className="narrative-body">⚠ Couldn’t load deal fields — {fieldsErr}</div>
            </div>
          ) : (
            <div className="section-block">
              <div className="section-title">DEAL FIELDS</div>
              <div className="narrative-body">Loading deal fields…</div>
            </div>
          )}
        </div>

        {/* Right: Loan Request (the long one) + Timing + Closed-loan + Narrative */}
        <div className="fo-col">
          {fields && <GroupBlock group="loan_request" title="LOAN REQUEST" icon="◆" data={fields} />}
          <SectionBlock title="TIMING" icon="◷">
            <DataRow label="Stage" value={detail.stage} highlight />
            <DataRow label="Close Date" value={fmtDate(detail.closeDate)} />
            <DataRow
              label="Days Since Activity"
              value={detail.daysSinceModified != null ? `${detail.daysSinceModified}d` : "—"}
              warn={(detail.daysSinceModified ?? 0) > 7}
            />
          </SectionBlock>
          {isClosedLoanStage(detail.stage) && (
            <ClosedLoanDetailsSection closedLoan={detail.closedLoan} />
          )}
          <SectionBlock title="INTAKE NARRATIVE" icon="▸">
            <div className="narrative-body">
              {detail.narrative?.trim() || "No intake narrative captured."}
            </div>
          </SectionBlock>
        </div>
      </div>
    </div>
  );
}

/** Render one curated group's overview fields as a section block. */
function GroupBlock({
  group,
  title,
  icon,
  data,
}: {
  group: DealFieldDef["group"];
  title: string;
  icon: string;
  data: DealFieldsResponse;
}) {
  const rows = DEAL_FIELDS.filter((f) => f.group === group && f.overview)
    .map((f) => ({ f, value: fieldValue(f, data) }))
    // Conditional fields (co-borrower) only render when populated.
    .filter(({ f, value }) => !(f.conditional && value === "—"));
  if (rows.length === 0) return null;
  return (
    <SectionBlock title={title} icon={icon}>
      {rows.map(({ f, value }) => (
        <DataRow key={f.apiName} label={f.label} value={value} highlight={HIGHLIGHT.has(f.apiName)} />
      ))}
    </SectionBlock>
  );
}

function SectionBlock(props: {
  title: string;
  icon?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={"section-block" + (props.className ? ` ${props.className}` : "")}>
      <div className="section-title">
        {props.icon && <span className="section-title-icon">{props.icon}</span>}
        {props.title}
      </div>
      {props.children}
    </div>
  );
}

function DataRow(props: { label: string; value: string; highlight?: boolean; warn?: boolean }) {
  return (
    <div className="data-row">
      <span className="data-label">{props.label}</span>
      <span
        className={[
          "data-value",
          props.highlight ? "highlight" : "",
          props.warn ? "warning" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {props.value}
      </span>
    </div>
  );
}
